/**
 * تحلیلِ آدرسِ رویدادها — سربرگِ «آدرس‌ها» در ادمین (WHOSUP-201).
 *
 * ═══ صورتِ مسئله ═══
 *
 * آدرسِ بیشترِ رویدادها یک **رشته‌ی آزاد** است که کرالر از دلِ صفحه‌ی یک
 * سایتِ غریبه بیرون کشیده. هیچ فرمی پرش نکرده، هیچ اعتبارسنجی‌ای ندیده، و
 * هیچ تستی هم نمی‌بیندش — چون از نظرِ تایپ‌اسکریپت یک `string | null` سالم
 * است. پس این سوال‌ها تا امروز بی‌جواب بودند:
 *
 *   • چند رویداد اصلاً آدرس ندارند، و چندتا آدرس دارند ولی مختصات نه؟
 *   • چند «آدرس» در واقع آدرس نیستند («TBA»، «Online»، یک لینک)؟
 *   • کدام‌ها ناقص‌اند — بی‌شماره‌ی پلاک، بی‌کدپستی، بی‌استان؟
 *   • کدام آدرس‌ها **یک جا**اند ولی چند جور نوشته شده‌اند («366 Bank St»
 *     و «366 Bank Street, Ottawa, ON»)؟ این یکی مستقیم به نقشه و به
 *     تشخیصِ تکراری‌ها ضربه می‌زند.
 *   • کدام شهر یا کدام منبع بدترین آدرس‌ها را می‌دهد؟
 *
 * ═══ چرا این‌جا و نه در SQL ═══
 *
 * همان تقسیمِ کاری که `sourceTest.ts` دارد و `journeyReport.ts`: مایگریشنِ
 * ۰۱۴۰ فقط **واقعیت** می‌دهد (این رشته، این شهر، این‌قدر رویداد، مختصات
 * دارد یا نه)، و **حکم** این‌جاست. دلیلش هم همان است:
 *
 *   • آستانه‌ها مدام عوض می‌شوند («آدرسِ زیرِ ۶ نویسه مشکوک است»، «بیش از
 *     ۱۲۰ نویسه یعنی پاراگراف نه آدرس»). اگر در SQL بودند، هر تغییرشان یک
 *     مایگریشن می‌خواست.
 *   • تجزیه‌ی رشته با regex در تایپ‌اسکریپت خواناتر و — مهم‌تر — با
 *     `npm test` آزمودنی است، بی‌شبکه و بی‌دیتابیس.
 *
 * پس این فایل خالص است و هیچ ایمپورتی به بیرونِ همین پوشه ندارد.
 *
 * ⚠ نسبتش با `address.ts`: آن یکی کارِ **نمایش** می‌کند (آدرسِ بلند را برای
 * کارت کوتاه می‌کند) و این یکی کارِ **تشخیص**. ثابت‌های مشترکشان — فهرستِ
 * استان‌ها و الگوی کدپستی — از همان‌جا می‌آید تا دو نسخه نشوند.
 */
import { CA_PROVINCES, CA_POSTAL_RE } from './address.ts';

// ═══════════ آنچه SQL می‌دهد ═══════════

/**
 * یک رشته‌ی آدرسِ یکتا در یک شهر، با هرچه رویِ آن نشسته.
 *
 * ⚠ ردیفِ بی‌آدرس این‌جا نمی‌آید — نبودنِ آدرس در `totals` شمرده می‌شود.
 * یک «گروهِ بی‌آدرس» در همین فهرست، همه‌ی تحلیل‌های رشته‌ای را آلوده می‌کرد.
 */
export interface AddressRow {
  address: string;
  city: string;
  /**
   * کدِ کشورِ شهر (از `cities.country_code`). `null` یعنی شهر پیدا نشد.
   *
   * ⚠ قاعده‌های کاناداییِ این فایل — کدپستی، کدِ استان، مرزِ مختصات — فقط
   * روی `CA` اجرا می‌شوند. دیتابیس شهرِ غیرکانادایی هم دارد (دبی، استکهلم)
   * و اعمالِ آن قاعده‌ها رویشان فقط صدها هشدارِ بی‌ربط می‌ساخت — که همان
   * چیزی است که یک فهرستِ ایراد را بعدِ یک هفته بی‌مصرف می‌کند.
   */
  country: string | null;
  /**
   * آدرس از کجا آمده: `event` یعنی `events.address` — همان چیزی که کاربر
   * می‌بیند. `venue` یعنی فقط روی ردیفِ `venues` نشسته و `mapEvent` اصلاً
   * نمی‌خواندش، پس **کاربر هیچ آدرسی نمی‌بیند**. سرِ مایگریشنِ ۰۱۴۰.
   */
  origin: 'event' | 'venue';
  /** چند رویدادِ زنده همین رشته را دارند. */
  events: number;
  /** از آن‌ها چندتا روی مکانشان آدرسِ **متفاوتی** هم دارند. */
  conflicts: number;
  /** چند `venue_id`ِ متفاوت. صفر یعنی هیچ‌کدام به مکانی وصل نیستند. */
  venues: number;
  /** چند جفتِ مختصاتِ متفاوت روی همین رشته. صفر یعنی هیچ مختصاتی نیست. */
  geo_points: number;
  /** یک نمونه‌ی مختصات از همین گروه (برای تشخیصِ مختصاتِ پرت). */
  lat: number | null;
  lng: number | null;
  sources: string[];
  sample_id: string;
  sample_title: string;
}

export interface AddressTotals {
  live_events: number;
  /** آدرس دارند — از هر کجا. */
  with_address: number;
  /** روی خودِ ردیف، یعنی کاربر می‌بیندش. */
  row_address: number;
  /** فقط روی مکان، یعنی داریمش و کاربر نمی‌بیندش. */
  venue_only: number;
  /** هر دو را دارند ولی یکی نیستند. */
  conflicts: number;
  /** رویدادهایی که شهرشان کانادا نیست — قاعده‌های کانادایی رویشان نمی‌رود. */
  outside_ca: number;
  with_geo: number;
  with_venue: number;
  distinct_addresses: number;
  /** ردیف‌هایی که اصلاً شمرده نشدند (rejected/merged/…) — فیلترِ بی‌صدا ممنوع. */
  excluded: number;
  /** چندتا از `distinct_addresses` واقعاً در `addresses` آمده‌اند. */
  listed: number;
  capped: boolean;
}

/** شمارشِ ساده‌ی یک شهر یا یک منبع — همه‌اش در SQL حساب می‌شود. */
export interface AddressBucket {
  key: string;
  /** فقط برای شهرها پر است؛ یک منبع می‌تواند چند کشور بدهد. */
  country: string | null;
  events: number;
  with_address: number;
  with_geo: number;
  with_venue: number;
  addresses: number;
}

// ═══════════ مختصات (مایگریشن ۰۱۴۱) ═══════════

/**
 * یک نقطه‌ی یکتا روی نقشه، با هرچه رویش نشسته.
 *
 * ⚠ نقطه‌ها **خیلی** کمتر از رویدادهایند (روی دادهٔ واقعی ۲۰۹ در برابر
 * ۲۲۱۸): یک سالن صد رویداد دارد و یک نقطه. برای همین این فهرست کامل
 * می‌آید و شمارشِ این فایل از رویش دقیق است، نه تخمینی از یک نمونه.
 */
export interface GeoPoint {
  lat: number;
  lng: number;
  city: string;
  country: string | null;
  events: number;
  venues: number;
  addresses: number;
  venue_name: string | null;
  address: string | null;
  /**
   * فاصله تا مرکزِ شهر، کیلومتر.
   *
   * ⚠ `null` یعنی **سنجیدنی نیست** (آن شهر خودش مختصات ندارد)، نه «صفر» و
   * نه «نزدیک». یکی گرفتنشان یعنی یک نقطه‌ی هزار کیلومتر دور در ستونِ
   * «نزدیک» می‌نشست.
   */
  km: number | null;
  sample_id: string;
  sample_title: string;
}

/** کشِ ژئوکدینگِ کرالر — `crawler.building_geocodes`. */
export interface GeocodeCache {
  total: number;
  generated: number;
  rejected: number;
  with_point: number;
  cities: number;
  versions: number;
  last_at: string | null;
  by_reason: { kind: string; n: number }[];
  by_model: { key: string; n: number; generated: number; rejected: number }[];
  by_city: { key: string; n: number; generated: number; rejected: number }[];
  recent: {
    key_kind: string;
    city: string;
    key_value: string;
    lat: number | null;
    lng: number | null;
    confidence: string;
    reasons: string[];
    decided_by: string;
    prompt_version: number;
    certain: boolean | null;
    matched: string | null;
    at: string;
  }[];
}

export interface GeoTotals {
  with_geo: number;
  /** روی ردیفِ مکان — همان چیزی که `mapEvent` اول سراغش می‌رود. */
  on_venue: number;
  /** روی خودِ ردیفِ رویداد. */
  on_row: number;
  distinct_points: number;
  listed_points: number;
  points_capped: boolean;
  /** رویدادهایی که شهرشان مرکز ندارد، پس فاصله‌شان سنجیدنی نیست. */
  no_city_point: number;
  /** `null` یعنی این دیتابیس اسکیمای کرالر را ندارد. */
  cache: GeocodeCache | null;
}

export interface EventAddresses {
  totals: AddressTotals;
  geo: GeoTotals;
  by_city: AddressBucket[];
  by_source: AddressBucket[];
  addresses: AddressRow[];
  points: GeoPoint[];
}

// ═══════════ تجزیه‌ی یک رشته ═══════════

/**
 * «Unit 3»، «Suite 200»، «#201»، «Apt 4B» — و معادلِ فرانسویش.
 *
 * ⚠ تکه‌ی گرفته‌شده **باید عدد داشته باشد** و این اجباری است نه سلیقه‌ای:
 * بی آن، «Rue Ste-Catherine» به «Ste» + «-Catherine» تجزیه می‌شد و نصفِ
 * آدرس‌های مونترال شماره‌ی واحدِ خیالی پیدا می‌کردند.
 */
const UNIT_RE = /(?:^|[\s,])(?:unit|suite|ste|apt|apartment|bureau|local|#)\s*\.?\s*(\d[\w-]*|[A-Za-z]\d[\w-]*)/i;

/** لینک، حتی بدونِ پروتکل: «www.x.com/y» هم لینک است. */
const URL_RE = /(?:https?:\/\/|www\.)\S+/i;
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.]+/;

/** شماره‌ی تلفنِ آمریکای شمالی — با یا بی‌کد، با خط‌تیره/نقطه/پرانتز. */
const PHONE_RE = /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}\b/;

/** جمله‌هایی که به‌جای آدرس نشسته‌اند. همه lowercase مقایسه می‌شوند. */
const NON_ADDRESS = [
  'tba', 'tbd', 'tbc', 'n/a', 'na', 'none', 'unknown', 'online', 'virtual',
  'zoom', 'webinar', 'livestream', 'live stream', 'remote', 'various',
  'various locations', 'multiple locations', 'see website', 'see link',
  'to be announced', 'to be determined', 'à déterminer', 'en ligne', 'ailleurs',
];

/** جهت‌های خیابان — «Bank St S» و «Bank Street South» یک جااند. */
const DIRECTIONS: Record<string, string> = {
  n: 'north', s: 'south', e: 'east', w: 'west',
  ne: 'northeast', nw: 'northwest', se: 'southeast', sw: 'southwest',
  north: 'north', south: 'south', east: 'east', west: 'west',
  northeast: 'northeast', northwest: 'northwest',
  southeast: 'southeast', southwest: 'southwest',
  // فرانسه — در گاتینو و مونترال کم نیست.
  nord: 'north', sud: 'south', est: 'east', ouest: 'west',
};

/** کوتاه‌نوشتِ نوعِ خیابان → شکلِ کامل. مبنای تشخیصِ «یک جا، چند املا». */
const STREET_TYPES: Record<string, string> = {
  st: 'street', str: 'street', street: 'street',
  ave: 'avenue', av: 'avenue', avenue: 'avenue',
  rd: 'road', road: 'road',
  dr: 'drive', drive: 'drive',
  blvd: 'boulevard', boul: 'boulevard', boulevard: 'boulevard',
  cres: 'crescent', cr: 'crescent', crescent: 'crescent',
  ct: 'court', court: 'court',
  pl: 'place', place: 'place',
  ln: 'lane', lane: 'lane',
  hwy: 'highway', highway: 'highway',
  pkwy: 'parkway', pky: 'parkway', parkway: 'parkway',
  ter: 'terrace', terrace: 'terrace',
  sq: 'square', square: 'square',
  cir: 'circle', circle: 'circle',
  trl: 'trail', trail: 'trail',
  way: 'way', walk: 'walk', row: 'row', close: 'close', gate: 'gate',
  // فرانسه
  rue: 'rue', chemin: 'chemin', ch: 'chemin', promenade: 'promenade',
};

/** «Canada» / «CA» ته آدرس فقط جا می‌گیرد — اپ فقط کاناداست. */
const COUNTRY_RE = /,?\s*(canada|can\.?|ca)\s*$/i;

const PROVINCE_SET = new Set(CA_PROVINCES);

/**
 * نامِ کاملِ استان → کدش.
 *
 * ⚠ این فهرست تزئینی نیست. بی آن، «1 Elgin Street, Ottawa, Ontario, K1P 5W1»
 * تکه‌ی «Ontario» را **شهر** حساب می‌کرد و رشته با شهرِ ردیف (ottawa)
 * ناهم‌خوان می‌شد — روی دادهٔ واقعی، ده‌ها هشدارِ دروغ از همین یک جا آمد.
 *
 * کلیدها بی‌اعراب و کوچک‌اند، چون جست‌وجو روی `deaccent(...).toLowerCase()`
 * انجام می‌شود؛ پس «Québec» هم با همان کلیدِ `quebec` پیدا می‌شود.
 */
const PROVINCE_NAMES: Record<string, string> = {
  ontario: 'ON',
  quebec: 'QC',
  'british columbia': 'BC', 'colombie-britannique': 'BC',
  alberta: 'AB',
  manitoba: 'MB',
  saskatchewan: 'SK',
  'nova scotia': 'NS', 'nouvelle-ecosse': 'NS',
  'new brunswick': 'NB', 'nouveau-brunswick': 'NB',
  'newfoundland and labrador': 'NL', newfoundland: 'NL',
  'prince edward island': 'PE', 'ile-du-prince-edouard': 'PE',
  'northwest territories': 'NT',
  nunavut: 'NU',
  yukon: 'YT',
};

/** بلندترین‌ها اول، وگرنه «newfoundland» جلوی «newfoundland and labrador» را می‌گیرد. */
const PROVINCE_NAME_KEYS = Object.keys(PROVINCE_NAMES).sort((a, b) => b.length - a.length);

/**
 * بی‌اعراب: «Montréal» = «Montreal».
 *
 * ⚠ بازه با `\u…` نوشته شده و نه با خودِ نویسه‌ها: نویسه‌های ترکیبی در
 * ویرایشگر روی نویسه‌ی قبلی‌شان می‌نشینند و رجکسی که با خودشان نوشته شده
 * باشد در دیف عملاً نامرئی است.
 */
const COMBINING_RE = new RegExp('[\\u0300-\\u036f]', 'g');
const deaccent = (s: string) => s.normalize('NFD').replace(COMBINING_RE, '');

export interface ParsedAddress {
  /** بخش‌های جداشده با کاما، تمیزشده و بی‌خالی. */
  parts: string[];
  /** پلاک — «366 Bank St» → «366». */
  streetNumber: string | null;
  /** خیابان، بی‌پلاک و بی‌واحد — «Bank St». */
  street: string | null;
  /** «Unit 3» → «3». */
  unit: string | null;
  postal: string | null;
  province: string | null;
  /** شهری که خودِ رشته ادعا می‌کند (بخشِ قبل از استان/کدپستی). */
  city: string | null;
  /** از پنج تکه چندتا آمده: پلاک، خیابان، شهر، استان، کدپستی. */
  completeness: number;
}

/**
 * رشته‌ی آزاد → پنج تکه‌ی شناخته‌شده.
 *
 * ⚠ عمداً بدونِ کتابخانه و بدونِ سرویسِ ژئوکدینگ: این تابع نمی‌خواهد آدرس
 * را **درست کند**، فقط می‌خواهد بگوید چه چیزی در آن **هست**. برای شمردن
 * همین کافی است، و چیزی به وابستگی‌های پروژه اضافه نمی‌کند.
 */
export function parseAddress(raw: string): ParsedAddress {
  const text = raw.replace(/\s+/g, ' ').trim();

  const postal = text.match(CA_POSTAL_RE)?.[0].toUpperCase().replace(/\s+/g, ' ') ?? null;
  const unit = text.match(UNIT_RE)?.[1] ?? null;

  // کدپستی و کشور از تکه‌ها بیرون می‌روند تا بخشِ «شهر» را گم نکنند.
  const body = text.replace(CA_POSTAL_RE, '').replace(COUNTRY_RE, '');
  const parts = body.split(',').map((p) => p.trim()).filter(Boolean);

  let province: string | null = null;
  const rest: string[] = [];
  for (const p of parts) {
    // استان چهار شکل دارد: تکه‌ی خودش («…, Ottawa, ON» / «…, Ottawa, Ontario»)
    // یا ته تکه‌ی شهر («Ottawa ON» / «Ottawa Ontario»).
    const alone = p.toUpperCase();
    if (PROVINCE_SET.has(alone)) { province = alone; continue; }

    const flat = deaccent(p).toLowerCase();
    if (PROVINCE_NAMES[flat]) { province = PROVINCE_NAMES[flat]; continue; }

    const m = p.match(/^(.*?)[\s]+([A-Za-z]{2})$/);
    if (m && PROVINCE_SET.has(m[2].toUpperCase())) {
      province = m[2].toUpperCase();
      if (m[1].trim()) rest.push(m[1].trim());
      continue;
    }

    const name = PROVINCE_NAME_KEYS.find((k) => flat.endsWith(` ${k}`));
    if (name) {
      province = PROVINCE_NAMES[name];
      const head = p.slice(0, p.length - name.length - 1).trim();
      if (head) rest.push(head);
      continue;
    }

    rest.push(p);
  }

  const first = rest[0] ?? null;
  // شهر = آخرین تکه، ولی فقط وقتی تکه‌ی دیگری هم هست؛ وگرنه «Bank St»
  // خودش شهر حساب می‌شد.
  //
  // ⚠ و فقط وقتی عدد ندارد: «355 Cooper Street, Room 14.» آخرین تکه‌اش
  // «Room 14.» است و شهر نیست — روی دادهٔ واقعی همین‌ها ناهم‌خوانیِ دروغ
  // می‌ساختند.
  const last = rest.length >= 2 ? rest[rest.length - 1] : null;
  const city = last && !/\d/.test(last) ? last : null;

  let streetNumber: string | null = null;
  let street: string | null = null;
  if (first) {
    const m = first.match(/^(\d+[A-Za-z]?)\s*[-–]?\s*(.+)$/);
    if (m) { streetNumber = m[1]; street = m[2].trim(); } else { street = first; }
    // «3-366 Bank St» و «366 Unit 3 Bank St»: واحد از اسمِ خیابان بیرون.
    street = street.replace(UNIT_RE, '').replace(/\s+/g, ' ').trim() || null;
  }

  const completeness = [streetNumber, street, city, province, postal]
    .filter(Boolean).length;

  return { parts, streetNumber, street, unit, postal, province, city, completeness };
}

/**
 * کلیدِ یکسان‌سازی: دو نوشتنِ متفاوت از یک جا باید یک رشته بدهند.
 *
 * «366 Bank St» و «366 Bank Street, Ottawa, ON K1S 3T5» هر دو →
 * `366 bank street`. شهر عمداً داخلِ کلید **نیست** — گروه‌بندی جدا با شهرِ
 * خودِ ردیف انجام می‌شود، که از شهرِ داخلِ رشته قابل‌اعتمادتر است.
 */
export function normalizeAddress(raw: string): string {
  const p = parseAddress(raw);
  const base = [p.streetNumber, p.street].filter(Boolean).join(' ');
  const words = deaccent(base || raw)
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => STREET_TYPES[w] ?? DIRECTIONS[w] ?? w);
  return words.join(' ');
}

// ═══════════ حکم ═══════════

export type Severity = 'fail' | 'warn' | 'info';

export type IssueCode =
  | 'not_an_address' | 'has_url' | 'too_short' | 'city_mismatch' | 'geo_outside_canada'
  | 'many_venues' | 'venue_only' | 'country_mismatch'
  | 'no_geo' | 'no_venue' | 'no_street_number' | 'no_postal' | 'no_province'
  | 'many_geo_points' | 'too_long' | 'has_phone' | 'venue_conflict'
  | 'country_suffix' | 'all_caps' | 'messy_separators';

/**
 * ⚠ سه شدت و نه دو: `info` چیزی است که **باید دیده شود ولی خرابی نیست**
 * («Canada» ته آدرس، حروفِ همه‌بزرگ). قاتیِ `warn` کردنشان یعنی ستونِ
 * هشدار پر می‌شود از چیزهایی که کسی قرار نیست درستشان کند — و آن ستون
 * همان لحظه بی‌معنی می‌شود.
 */
export const ADDRESS_ISSUES: Record<IssueCode, {
  label: string; help: string; severity: Severity;
}> = {
  not_an_address: {
    label: 'آدرس نیست',
    help: 'رشته‌ای مثلِ «TBA»، «Online»، «Various locations» — کاربر رویش هیچ کاری نمی‌تواند بکند.',
    severity: 'fail',
  },
  has_url: {
    label: 'لینک یا ایمیل',
    help: 'به‌جای آدرس، آدرسِ اینترنتی درآمده. تقریباً همیشه یعنی استخراجگر تکه‌ی اشتباهی از صفحه را برداشته.',
    severity: 'fail',
  },
  too_short: {
    label: 'خیلی کوتاه',
    help: 'کمتر از ۶ نویسه. آدرس نیست، تکه‌ی جامانده از یک آدرس است.',
    severity: 'fail',
  },
  city_mismatch: {
    label: 'شهرش نمی‌خوانَد',
    help: 'شهری که داخلِ رشته نوشته شده با شهرِ خودِ رویداد یکی نیست — یا رویداد در شهرِ غلطی نشسته، یا آدرس مالِ رویدادِ دیگری است.',
    severity: 'fail',
  },
  geo_outside_canada: {
    label: 'مختصاتِ پرت',
    help: 'lat/lng بیرونِ مرزِ تقریبیِ کاناداست. معمولاً یعنی طول و عرض جابه‌جا شده‌اند یا علامتِ منفیِ طول افتاده.',
    severity: 'fail',
  },
  many_venues: {
    label: 'یک آدرس، چند مکان',
    help: 'همین رشته به بیش از یک ردیفِ venue وصل شده — یعنی یک جا دو بار در جدولِ مکان‌ها ساخته شده.',
    severity: 'fail',
  },
  country_mismatch: {
    label: 'کشورش نمی‌خوانَد',
    help: 'آدرس کدپستی یا کدِ استانِ کانادایی دارد ولی شهرِ رویداد در کانادا ثبت نشده. تقریباً همیشه یعنی city_id روی شهرِ هم‌نامِ کشورِ دیگری نشسته — «London, ON» که به London انگلستان وصل شده.',
    severity: 'fail',
  },
  venue_only: {
    label: 'کاربر نمی‌بیندش',
    help: 'آدرس فقط روی ردیفِ venue نشسته و اپ `events.address` را نشان می‌دهد — پس این آدرس را داریم و هیچ‌کس نمی‌بیندش.',
    severity: 'fail',
  },
  no_geo: {
    label: 'بی‌مختصات',
    help: 'آدرس هست ولی lat/lng نیست. روی نقشه نمی‌نشیند و مرتب‌سازیِ «نزدیکِ من» نادیده‌اش می‌گیرد.',
    severity: 'warn',
  },
  no_venue: {
    label: 'بی‌مکان',
    help: 'به هیچ ردیفی در جدولِ venues وصل نیست، پس نامِ مکان و اطلاعاتش را ندارد.',
    severity: 'warn',
  },
  no_street_number: {
    label: 'بی‌پلاک',
    help: 'اولین تکه با عدد شروع نمی‌شود. گاهی درست است («Gatineau Park») ولی بیشترِ وقت‌ها یعنی پلاک گم شده.',
    severity: 'warn',
  },
  no_postal: {
    label: 'بی‌کدپستی',
    help: 'کدپستیِ کانادایی در رشته نیست. برای ژئوکدکردن دقیق‌ترین تکه همین است.',
    severity: 'warn',
  },
  no_province: {
    label: 'بی‌استان',
    help: 'کدِ استان (ON/QC/…) در رشته نیست.',
    severity: 'warn',
  },
  many_geo_points: {
    label: 'یک آدرس، چند مختصات',
    help: 'رویدادهایی با همین رشته مختصاتِ متفاوت گرفته‌اند. دست‌کم یکی‌شان غلط است.',
    severity: 'warn',
  },
  too_long: {
    label: 'خیلی بلند',
    help: 'بیش از ۱۲۰ نویسه — معمولاً یعنی یک پاراگرافِ راهنما به‌جای آدرس افتاده داخلش.',
    severity: 'warn',
  },
  has_phone: {
    label: 'شماره‌ی تلفن دارد',
    help: 'تلفن داخلِ آدرس مانده. بی‌خطر است ولی نشانه‌ی همان استخراجِ نادقیق است.',
    severity: 'warn',
  },
  venue_conflict: {
    label: 'با آدرسِ مکان نمی‌خوانَد',
    help: 'هم روی ردیفِ رویداد آدرس هست هم روی مکانش، و یکی نیستند. کاربر آنِ روی ردیف را می‌بیند؛ دست‌کم یکی از دو تا کهنه است.',
    severity: 'warn',
  },
  country_suffix: {
    label: '«Canada» ته آدرس',
    help: 'اپ فقط کاناداست؛ این تکه فقط جا می‌گیرد.',
    severity: 'info',
  },
  all_caps: {
    label: 'همه بزرگ',
    help: 'کلِ رشته حروفِ بزرگ است — کپیِ مستقیم از یک سایتِ قدیمی.',
    severity: 'info',
  },
  messy_separators: {
    label: 'نقطه‌گذاریِ شلخته',
    help: 'کامای تکراری، کامای آخر، یا خطِ جدید داخلِ رشته.',
    severity: 'info',
  },
};

/** مرزِ تقریبیِ کانادا. دقتش مهم نیست؛ کارش گرفتنِ «۰,۰» و جابه‌جاییِ lat/lng است. */
const CA_BOUNDS = { latMin: 41, latMax: 84, lngMin: -142, lngMax: -51 };

const SEVERITY_RANK: Record<Severity, number> = { fail: 3, warn: 2, info: 1 };

/** بدترین شدت در یک فهرست؛ فهرستِ خالی یعنی سالم. */
export function worstSeverity(codes: IssueCode[]): Severity | null {
  let best: Severity | null = null;
  for (const c of codes) {
    const s = ADDRESS_ISSUES[c].severity;
    if (!best || SEVERITY_RANK[s] > SEVERITY_RANK[best]) best = s;
  }
  return best;
}

/**
 * همه‌ی ایرادهای یک ردیف، به ترتیبِ شدت.
 *
 * ⚠ `not_an_address` بقیه‌ی ایرادهای رشته‌ای را خفه می‌کند: «TBA» طبعاً
 * بی‌پلاک و بی‌کدپستی است و شمردنِ آن سه تا به‌جای یکی، فهرستِ ایرادها را
 * با ردیف‌های تکراری پر می‌کرد و عددِ «بی‌کدپستی» را دروغ می‌گفت.
 */
export function addressIssues(row: AddressRow): IssueCode[] {
  const out: IssueCode[] = [];
  const raw = row.address;
  const flat = raw.replace(/\s+/g, ' ').trim();
  const lower = deaccent(flat).toLowerCase().replace(/[.\s]+$/, '');

  /**
   * ایرادهایی که از خودِ ردیف می‌آیند و نه از رشته — مختصات و مکان.
   *
   * تابع است و نه چند خطِ ساده، چون هر سه راهِ زودخروجِ بالا هم باید صدایش
   * بزنند: «TBA»ای که مختصاتِ غلط هم دارد، دو خبر است نه یکی.
   */
  // ⚠ کدپستی، کدِ استان و مرزِ مختصات همه‌شان کانادایی‌اند. روی شهرِ دبی و
  // استکهلم اجرایشان یعنی صدها هشدارِ بی‌ربط، و فهرستی که هیچ‌کس دیگر
  // نگاهش نمی‌کند.
  const ca = row.country === 'CA';

  const rowIssues = (): void => {
    if (row.origin === 'venue') out.push('venue_only');
    else if (row.conflicts > 0) out.push('venue_conflict');
    if (row.geo_points === 0) out.push('no_geo');
    else if (row.geo_points > 1) out.push('many_geo_points');
    if (ca && row.lat != null && row.lng != null
      && (row.lat < CA_BOUNDS.latMin || row.lat > CA_BOUNDS.latMax
        || row.lng < CA_BOUNDS.lngMin || row.lng > CA_BOUNDS.lngMax)) {
      out.push('geo_outside_canada');
    }
    if (row.venues === 0) out.push('no_venue');
    else if (row.venues > 1) out.push('many_venues');
  };

  if (NON_ADDRESS.includes(lower)) {
    out.push('not_an_address');
    rowIssues();
    return out;
  }
  if (URL_RE.test(flat) || EMAIL_RE.test(flat)) {
    out.push('has_url');
    rowIssues();
    return out;
  }
  if (flat.length < 6) {
    out.push('too_short');
    rowIssues();
    return out;
  }

  const p = parseAddress(flat);

  if (p.city && row.city) {
    const a = deaccent(p.city).toLowerCase();
    const b = deaccent(row.city).toLowerCase();
    // «Ottawa» داخلِ «Ottawa (Nepean)» و برعکس هنوز همان شهر است؛ فقط
    // وقتی هیچ‌کدام دیگری را در بر نگیرد ناهم‌خوانی است.
    if (!a.includes(b) && !b.includes(a)) out.push('city_mismatch');
  }
  if (!p.streetNumber) out.push('no_street_number');
  if (ca && !p.postal) out.push('no_postal');
  if (ca && !p.province) out.push('no_province');
  // برعکسش: آدرس کانادایی است و شهر نه. این‌جا قاعده‌های کانادایی خاموش‌اند
  // (ca غلط است)، پس بی این چک، آن ردیف کاملاً بی‌ایراد به نظر می‌رسید.
  if (!ca && (p.postal || p.province)) out.push('country_mismatch');
  if (flat.length > 120) out.push('too_long');
  if (PHONE_RE.test(flat)) out.push('has_phone');

  rowIssues();

  if (COUNTRY_RE.test(raw.trim())) out.push('country_suffix');
  if (/[A-Z]/.test(raw) && raw === raw.toUpperCase()) out.push('all_caps');
  if (/,\s*,|,\s*$|\n|\r|\s{2,}/.test(raw)) out.push('messy_separators');

  return out.sort((x, y) =>
    SEVERITY_RANK[ADDRESS_ISSUES[y].severity] - SEVERITY_RANK[ADDRESS_ISSUES[x].severity]);
}

// ═══════════ گزارشِ کامل ═══════════

export interface AddressSample {
  address: string;
  city: string;
  events: number;
  id: string;
  title: string;
}

export interface AddressIssueGroup {
  code: IssueCode;
  label: string;
  help: string;
  severity: Severity;
  /** چند رشته‌ی یکتا این ایراد را دارند. */
  addresses: number;
  /** روی‌هم چند رویدادِ زنده درگیرِ آن‌اند. */
  events: number;
  samples: AddressSample[];
}

/** چند نوشتنِ متفاوت از یک جا، در یک شهر. */
export interface AddressVariantGroup {
  key: string;
  city: string;
  events: number;
  variants: { address: string; events: number; geo_points: number }[];
}

export interface AddressProblem extends AddressSample {
  issues: IssueCode[];
  severity: Severity;
  /** کدام منبع(ها) این رشته را ساخته‌اند — «تقصیرِ کیست» بی این جواب ندارد. */
  sources: string[];
}

// ═══════════ حکمِ مختصات ═══════════

export type GeoBand = 'near' | 'mid' | 'far' | 'wild' | 'unmeasured';

/**
 * مرزهای فاصله از مرکزِ شهر، کیلومتر.
 *
 * ⚠ این‌جاست و نه در SQL، دقیقاً به همان دلیلی که سرِ این فایل نوشته شده:
 * این‌ها **حکم**اند نه حساب. مایگریشن ۰۱۴۱ فقط کیلومتر می‌دهد؛ اینکه ۲۶
 * کیلومتر «حومه» است یا «پرت»، تصمیمی است که با دیدنِ داده عوض می‌شود و
 * نباید هر بار یک مایگریشن بخواهد.
 *
 * اعداد از روی دادهٔ واقعی انتخاب شدند: مزرعه‌ی ساندرز واقعاً ۳۴ کیلومتر
 * بیرونِ اتاواست و خرابی نیست، ولی «Canada Life Place»ِ لندنِ انتاریو روی
 * مختصاتِ لندنِ انگلستان نشسته — ۵۸۷۶ کیلومتر.
 */
export const GEO_BANDS = { near: 5, mid: 25, far: 100 };

export const GEO_BAND_LABEL: Record<GeoBand, string> = {
  near: 'داخلِ شهر',
  mid: 'حومه',
  far: 'دور',
  wild: 'پرت',
  unmeasured: 'سنجیدنی نیست',
};

export const GEO_BAND_HELP: Record<GeoBand, string> = {
  near: `کمتر از ${GEO_BANDS.near} کیلومتر از مرکزِ شهر.`,
  mid: `${GEO_BANDS.near} تا ${GEO_BANDS.mid} کیلومتر — حومه، و معمولاً درست.`,
  far: `${GEO_BANDS.mid} تا ${GEO_BANDS.far} کیلومتر. گاهی واقعی است (مزرعه، پارکِ ملی) و گاهی یعنی ژئوکدر شهرِ دیگری را گرفته.`,
  wild: `بیش از ${GEO_BANDS.far} کیلومتر. تقریباً همیشه خرابی است — شهرِ هم‌نام در کشورِ دیگر، یا جابه‌جاییِ lat/lng.`,
  unmeasured: 'شهرِ این رویداد خودش مختصات ندارد، پس فاصله‌اش سنجیدنی نیست. این با «نزدیک است» یکی نیست.',
};

/** نقطه در کدام باند می‌افتد. */
export function geoBand(km: number | null): GeoBand {
  if (km == null) return 'unmeasured';
  if (km < GEO_BANDS.near) return 'near';
  if (km < GEO_BANDS.mid) return 'mid';
  if (km < GEO_BANDS.far) return 'far';
  return 'wild';
}

/** نقطه‌ای که ارزشِ نگاه کردن دارد — «دور» و بدتر. */
export const isSuspectPoint = (km: number | null): boolean => {
  const b = geoBand(km);
  return b === 'far' || b === 'wild';
};

export interface GeoBandRow {
  band: GeoBand;
  label: string;
  help: string;
  points: number;
  events: number;
  /** دورترینِ همین باند — تا معلوم باشد لبه‌اش کجاست. */
  maxKm: number | null;
}

export interface GeoReport {
  totals: GeoTotals;
  bands: GeoBandRow[];
  /** دورترین نقطه‌ها، از بد به بدتر. */
  outliers: GeoPoint[];
  /** شلوغ‌ترین نقطه‌ها — یک سالن با صد رویداد. */
  piles: GeoPoint[];
  /** بزرگ‌ترین انباشت روی یک نقطه. */
  biggestPile: number;
}

const MAX_OUTLIERS = 25;
const MAX_PILES = 15;

/**
 * از فهرستِ نقطه‌ها، گزارشِ مختصات.
 *
 * جدا از `buildAddressReport` صدا زده نمی‌شود؛ جدا **نوشته** شده چون سوالش
 * جداست: آن یکی می‌پرسد «این رشته آدرسِ درستی است؟» و این یکی «این نقطه جای
 * درستی است؟» — و دومی هیچ کاری با متنِ آدرس ندارد.
 */
export function buildGeoReport(totals: GeoTotals, points: GeoPoint[]): GeoReport {
  const byBand = new Map<GeoBand, { points: number; events: number; maxKm: number | null }>();

  for (const p of points) {
    const band = geoBand(p.km);
    const b = byBand.get(band) ?? { points: 0, events: 0, maxKm: null };
    b.points += 1;
    b.events += p.events;
    if (p.km != null && (b.maxKm == null || p.km > b.maxKm)) b.maxKm = p.km;
    byBand.set(band, b);
  }

  const order: GeoBand[] = ['near', 'mid', 'far', 'wild', 'unmeasured'];
  const bands: GeoBandRow[] = order.map((band) => ({
    band,
    label: GEO_BAND_LABEL[band],
    help: GEO_BAND_HELP[band],
    points: byBand.get(band)?.points ?? 0,
    events: byBand.get(band)?.events ?? 0,
    maxKm: byBand.get(band)?.maxKm ?? null,
  }));

  const outliers = points
    .filter((p) => isSuspectPoint(p.km))
    .sort((a, b) => (b.km ?? 0) - (a.km ?? 0))
    .slice(0, MAX_OUTLIERS);

  const piles = [...points]
    .sort((a, b) => b.events - a.events || b.venues - a.venues)
    .slice(0, MAX_PILES);

  return {
    totals,
    bands,
    outliers,
    piles,
    biggestPile: points.reduce((m, p) => Math.max(m, p.events), 0),
  };
}

export interface AddressReport {
  totals: AddressTotals;
  by_city: AddressBucket[];
  by_source: AddressBucket[];
  /** چند رشته/رویداد سالم‌اند، چند تا فقط هشدار دارند، چند تا خراب‌اند. */
  quality: {
    cleanAddresses: number; warnAddresses: number; failAddresses: number;
    cleanEvents: number; warnEvents: number; failEvents: number;
  };
  /**
   * ۰ تا ۵ — از پنج تکه‌ی آدرس چندتا آمده.
   *
   * ⚠ فقط رشته‌های کانادایی شمرده می‌شوند: دو تکه از پنج تکه (کدپستی و کدِ
   * استان) کانادایی‌اند، پس آدرسِ دبی همیشه سقفش ۳ می‌شد و هیستوگرام را به
   * سمتِ چپ می‌کشید بی آنکه چیزی خراب باشد. `completenessBase` مخرجِ واقعی
   * را می‌گوید.
   */
  completeness: { level: number; addresses: number; events: number }[];
  completenessBase: number;
  issues: AddressIssueGroup[];
  variants: AddressVariantGroup[];
  /** بدترین‌ها، برای وقتی که آدم می‌خواهد از یک جایی شروع کند. */
  problems: AddressProblem[];
  /** مختصات — سوالِ جدا، گزارشِ جدا. نگاه کن به `buildGeoReport`. */
  geo: GeoReport;
  /** نقطه‌های یکتا، خوراکِ نقشه‌ی ته صفحه. */
  points: GeoPoint[];
}

const SAMPLES_PER_ISSUE = 3;
const MAX_VARIANT_GROUPS = 40;
const MAX_PROBLEMS = 60;

/**
 * از واقعیت‌های SQL، یک گزارشِ خوانا.
 *
 * ترتیبِ همه‌ی فهرست‌ها «چند رویداد درگیرش‌اند» است و نه «چند رشته»: یک
 * ایرادِ نادر روی آدرسی که صد رویداد دارد مهم‌تر از صد ایرادِ تک‌رویدادی
 * است، و مرتب‌سازی بر اساسِ شمارِ رشته دقیقاً برعکسش را نشان می‌داد.
 */
export function buildAddressReport(data: EventAddresses): AddressReport {
  const rows = data.addresses;

  const quality = {
    cleanAddresses: 0, warnAddresses: 0, failAddresses: 0,
    cleanEvents: 0, warnEvents: 0, failEvents: 0,
  };
  const byIssue = new Map<IssueCode, { addresses: number; events: number; samples: AddressSample[] }>();
  const byLevel = new Map<number, { addresses: number; events: number }>();
  const byKey = new Map<string, AddressVariantGroup>();
  const problems: AddressProblem[] = [];
  let completenessBase = 0;

  for (const r of rows) {
    const sample: AddressSample = {
      address: r.address, city: r.city, events: r.events,
      id: r.sample_id, title: r.sample_title,
    };
    const codes = addressIssues(r);
    const worst = worstSeverity(codes);

    if (worst === 'fail') { quality.failAddresses += 1; quality.failEvents += r.events; }
    else if (worst === 'warn') { quality.warnAddresses += 1; quality.warnEvents += r.events; }
    else { quality.cleanAddresses += 1; quality.cleanEvents += r.events; }

    for (const c of codes) {
      const g = byIssue.get(c) ?? { addresses: 0, events: 0, samples: [] };
      g.addresses += 1;
      g.events += r.events;
      if (g.samples.length < SAMPLES_PER_ISSUE) g.samples.push(sample);
      byIssue.set(c, g);
    }

    if (r.country === 'CA') {
      const level = parseAddress(r.address).completeness;
      const lv = byLevel.get(level) ?? { addresses: 0, events: 0 };
      lv.addresses += 1;
      lv.events += r.events;
      byLevel.set(level, lv);
      completenessBase += 1;
    }

    // کلید شهر را هم می‌گیرد: دو «100 Main St» در دو شهر یک جا نیستند.
    const key = `${normalizeAddress(r.address)}|${deaccent(r.city).toLowerCase()}`;
    const vg = byKey.get(key) ?? { key, city: r.city, events: 0, variants: [] };
    vg.events += r.events;
    // ⚠ **همان رشته** می‌تواند دو ردیف باشد — یکی از `events.address` و یکی
    // از `venues.address` — و شمردنشان به‌عنوان «دو املا» دروغ است: املا یکی
    // است، محلِ ذخیره دو تا.
    const same = vg.variants.find((v) => v.address === r.address);
    if (same) {
      same.events += r.events;
      same.geo_points = Math.max(same.geo_points, r.geo_points);
    } else {
      vg.variants.push({ address: r.address, events: r.events, geo_points: r.geo_points });
    }
    byKey.set(key, vg);

    if (worst && worst !== 'info') {
      problems.push({ ...sample, issues: codes, severity: worst, sources: r.sources });
    }
  }

  const issues: AddressIssueGroup[] = [...byIssue.entries()]
    .map(([code, g]) => ({
      code,
      label: ADDRESS_ISSUES[code].label,
      help: ADDRESS_ISSUES[code].help,
      severity: ADDRESS_ISSUES[code].severity,
      addresses: g.addresses,
      events: g.events,
      samples: g.samples,
    }))
    .sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]
      || b.events - a.events);

  const completeness = [0, 1, 2, 3, 4, 5].map((level) => ({
    level,
    addresses: byLevel.get(level)?.addresses ?? 0,
    events: byLevel.get(level)?.events ?? 0,
  }));

  const variants = [...byKey.values()]
    .filter((g) => g.variants.length > 1)
    .map((g) => ({
      ...g,
      variants: [...g.variants].sort((a, b) => b.events - a.events),
    }))
    .sort((a, b) => b.variants.length - a.variants.length || b.events - a.events)
    .slice(0, MAX_VARIANT_GROUPS);

  problems.sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]
    || b.events - a.events
    || b.issues.length - a.issues.length);

  return {
    totals: data.totals,
    by_city: data.by_city,
    by_source: data.by_source,
    quality,
    completeness,
    completenessBase,
    issues,
    variants,
    problems: problems.slice(0, MAX_PROBLEMS),
    geo: buildGeoReport(data.geo, data.points),
    points: data.points,
  };
}
